"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { Square } from "chess.js";
import { Board2D } from "@/components/board/Board2D";
import type { Arrow } from "@/components/board/interaction";
import { MOVE_DELAY, attackedSquares, beatDuration, compileTutorial } from "@/lib/tutorial/compile";
import type { Overlays, Tutorial } from "@/lib/tutorial/types";
import { playMoveSound } from "@/lib/sound";
import { useEngaged } from "@/lib/engagement";
import { TONE } from "./tones";
import { ChessArtwork } from "@/components/site/ChessArtwork";

const Stage = dynamic(() => import("./Stage"), {
  ssr: false,
  loading: () => <div className="grid h-full w-full place-items-center text-sm text-muted">Setting up the board…</div>,
});

const noop = () => () => {};
let webglAvailable: boolean | undefined;

function useWebGL() {
  // Probing creates a WebGL context, which is slow without a GPU; nothing 3D loads before the
  // reader interacts, so until then assume support, as the server render does.
  const engaged = useEngaged();
  return useSyncExternalStore(
    noop,
    () => {
      if (!engaged) return true;
      try {
        if (webglAvailable !== undefined) return webglAvailable;
        const gl = document.createElement("canvas").getContext("webgl2");
        webglAvailable = !!gl;
        gl?.getExtension("WEBGL_lose_context")?.loseContext();
        return webglAvailable;
      } catch {
        return false;
      }
    },
    () => true,
  );
}

function useReducedMotion() {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia("(prefers-reduced-motion: reduce)");
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    },
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    () => false,
  );
}

function merge(a: Overlays, b?: Overlays): Overlays {
  if (!b) return a;
  return {
    arrows: [...(a.arrows ?? []), ...(b.arrows ?? [])],
    squares: [...(a.squares ?? []), ...(b.squares ?? [])],
    attacks: [...(a.attacks ?? []), ...(b.attacks ?? [])],
    labels: [...(a.labels ?? []), ...(b.labels ?? [])],
    spotlight: [...(a.spotlight ?? []), ...(b.spotlight ?? [])],
  };
}

function hexA(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

const SPEEDS = [0.75, 1, 1.5];

/**
 * Plays a lesson as a short film: each beat moves the camera, draws its arrows and
 * highlights, plays its move, and holds long enough to read the caption.
 */
export function TutorialPlayer({ tutorial, theatre = false, autoStart = false, study = false }: { tutorial: Tutorial; theatre?: boolean; autoStart?: boolean; study?: boolean }) {
  const frames = useMemo(() => compileTutorial(tutorial), [tutorial]);
  const webgl = useWebGL();
  const reducedMotion = useReducedMotion();
  const [started, setStarted] = useState(autoStart || study);
  const [index, setIndex] = useState(0);
  const [sceneVersion, setSceneVersion] = useState(0);
  const [playing, setPlaying] = useState(autoStart && !study && !reducedMotion);
  const [phase, setPhase] = useState({ index: -1, after: false });
  const [speed, setSpeed] = useState(1);
  const [free, setFree] = useState(false);
  const [flat, setFlat] = useState(false);
  // Time already spent on the current scene, in beat time (1x speed). Keyed by scene so
  // restarting the same step, which keeps the index, starts from zero.
  const elapsed = useRef({ scene: 0, ms: 0 });
  const scene = useRef(0);
  const speedRef = useRef(speed);
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);
  const fill = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);

  const frame = frames[index];
  const after = phase.index === index && phase.after;
  const durations = useMemo(() => frames.map((f) => beatDuration(f.beat)), [frames]);
  const total = durations.reduce((a, b) => a + b, 0);

  const goTo = useCallback(
    (i: number) => {
      const next = Math.max(0, Math.min(frames.length - 1, i));
      scene.current += 1;
      elapsed.current = { scene: scene.current, ms: 0 };
      setPhase({ index: -1, after: false });
      setSceneVersion(scene.current);
      setIndex(next);
    },
    [frames.length],
  );

  // Play the beat's move (and reveal its "after" overlays) once the camera has arrived.
  useEffect(() => {
    if (!started) return;
    const f = frames[index];
    const t = setTimeout(() => {
      setPhase({ index, after: true });
      if (f.move) playMoveSound(f.move.san.includes("x") ? "capture" : f.move.san.includes("+") || f.move.san.includes("#") ? "check" : "move");
    }, MOVE_DELAY / speedRef.current);
    return () => clearTimeout(t);
  }, [index, started, frames, sceneVersion]);

  // Advance to the next beat when this one has been on screen long enough.
  useEffect(() => {
    if (!started) return;
    const dur = durations[index];
    if (elapsed.current.scene !== sceneVersion) elapsed.current = { scene: sceneVersion, ms: 0 };
    const done = elapsed.current.ms;
    const remaining = Math.max(0, dur - done) / speed;
    const bar = fill.current;
    let anim: Animation | undefined;
    if (bar) {
      const from = Math.min(100, (done / dur) * 100);
      bar.style.width = `${from}%`;
      if (playing && bar.animate) anim = bar.animate([{ width: `${from}%` }, { width: "100%" }], { duration: remaining, fill: "forwards" });
    }
    if (!playing) return () => anim?.cancel();
    const startedAt = performance.now();
    const t = setTimeout(() => {
      if (index < frames.length - 1) goTo(index + 1);
      else setPlaying(false);
    }, remaining);
    return () => {
      clearTimeout(t);
      if (elapsed.current.scene === sceneVersion) elapsed.current.ms += (performance.now() - startedAt) * speed;
      if (anim) {
        // Keep the bar where it stopped when pausing; the element is gone if the step changed.
        try {
          if (bar?.isConnected) anim.commitStyles();
        } catch {
          /* not rendered: nothing to keep */
        }
        anim.cancel();
      }
    };
  }, [index, playing, started, durations, speed, frames.length, goTo, sceneVersion]);

  const start = () => {
    setStarted(true);
    setPlaying(true);
    goTo(0);
    requestAnimationFrame(() => root.current?.focus());
  };

  const replay = () => {
    goTo(0);
    setPlaying(true);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if ((e.target as HTMLElement).closest("button, a, input, select")) return;
    if (!started) return;
    if (e.key === " " || e.key === "k") setPlaying((p) => !p);
    else if (e.key === "ArrowRight") goTo(index + 1);
    else if (e.key === "ArrowLeft") goTo(index - 1);
    else return;
    e.preventDefault();
  };

  const fen = after ? frame.fenAfter : frame.fen;
  const lastMove = after && frame.move ? { from: frame.move.from, to: frame.move.to } : frame.previousMove;
  const beat = frame.beat;
  const overlays: Overlays = after ? (beat.move ? (beat.after ?? {}) : merge(beat, beat.after)) : beat;
  const overlayKey = `${index}-${after ? 1 : 0}`;
  const ended = index === frames.length - 1 && !playing && after;
  const use3D = webgl && !flat;

  const moveLabel = frame.move
    ? `${frame.fen.split(" ")[5]}${frame.move.color === "w" ? "." : "..."} ${frame.move.san}`
    : null;

  // 2D fallback overlays: arrows for moves and attacks, tints for squares.
  const arrows2d: Arrow[] = [
    ...(overlays.arrows ?? []).map((a) => ({ from: a.from, to: a.to, color: hexA(TONE[a.tone ?? "gold"], 0.85) })),
    ...(overlays.attacks ?? []).flatMap((a) => (a.to ?? attackedSquares(fen, a.from)).map((t) => ({ from: a.from, to: t as Square, color: hexA(TONE[a.tone ?? "red"], 0.55) }))),
  ];
  const tints = Object.fromEntries((overlays.squares ?? []).map((s) => [s.sq, hexA(TONE[s.tone ?? "gold"], 0.45)]));

  return (
    <div className={study ? "lesson-film-layout" : undefined}>
    <div
      ref={root}
      tabIndex={-1}
      onKeyDown={onKey}
      className="relative overflow-hidden rounded-2xl lesson-player bg-surface text-text shadow-xl ring-1 ring-black/20 outline-none"
      aria-label={`${tutorial.title}, a 3D lesson`}
      role="region"
    >
      <div className="relative aspect-square w-full sm:aspect-[16/10]">
        <div
          className="absolute inset-0"
          style={{ background: "var(--scene)" }}
          aria-hidden
        />
        {started && use3D && (
          <div className="absolute inset-0">
            <Stage fen={fen} lastMove={lastMove} overlays={overlays} overlayKey={overlayKey} shot={beat.camera} free={free} reducedMotion={reducedMotion} study={study} />
          </div>
        )}
        {started && !use3D && (
          <div className={`absolute inset-0 grid place-items-center p-4 ${study ? "pt-16" : "sm:pb-28"}`}>
            <div className="aspect-square h-full max-w-full">
              <Board2D fen={fen} lastMove={lastMove} arrows={arrows2d} tints={tints} label={tutorial.title} />
            </div>
          </div>
        )}

        {!started && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
            <div className="lesson-cover-art"><ChessArtwork piece={tutorial.kind === "rule" ? "k" : "n"} variant={1}/></div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">3D lesson · {frames.length} steps · {Math.round(total / 1000 / 10) * 10 || 30} seconds</p>
            <h3 className="max-w-xl font-display text-2xl sm:text-4xl">{tutorial.title}</h3>
            <p className="max-w-lg text-sm text-muted sm:text-base">{tutorial.summary}</p>
            <button
              onClick={start}
              className="mt-2 flex items-center gap-2 rounded-full bg-accent px-6 py-3 font-semibold text-accent-ink shadow-lg transition hover:scale-[1.03] hover:opacity-90"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
              Play the lesson
            </button>
            {!webgl && <p className="text-xs text-muted">3D is not available in this browser, so the lesson plays on a 2D board.</p>}
          </div>
        )}

        {started && (
          <>
            {/* Top bar */}
            <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-2 bg-gradient-to-b from-bg/90 to-transparent p-3 sm:p-4">
              <div className="pointer-events-auto min-w-0">
                <p className="truncate text-xs font-semibold uppercase tracking-[0.18em] text-accent">{tutorial.kind === "rule" ? "Rule" : tutorial.kind === "article" ? "Journal lesson" : "Opening"}</p>
                <p className="truncate font-display text-base sm:text-lg">{tutorial.title}</p>
              </div>
              <div className="pointer-events-auto flex shrink-0 gap-1.5">
                {use3D && (
                  <ChipButton active={free} onClick={() => setFree((f) => !f)} label={free ? "Return to the guided camera" : "Orbit the board yourself"}>
                    {free ? "Guided camera" : "Free camera"}
                  </ChipButton>
                )}
                {webgl && (
                  <ChipButton active={flat} onClick={() => setFlat((f) => !f)} label="Toggle 2D board">
                    {flat ? "3D" : "2D"}
                  </ChipButton>
                )}
                {!theatre && tutorial.kind !== "article" && (
                  <Link
                    href={`/lessons/${tutorial.slug}`}
                    className="rounded-full bg-surface-2/80 px-3 py-1 text-xs font-medium backdrop-blur hover:bg-accent-soft"
                    aria-label="Open in theatre mode"
                  >
                    Theatre
                  </Link>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {started && (
        <>
            {/* Caption: below the board on phones, overlaid on wider screens */}
            <div className={study ? "relative border-t border-border bg-surface px-5 pb-4 pt-4" : "relative px-4 pb-3 pt-3 sm:absolute sm:inset-x-0 sm:bottom-0 sm:bg-gradient-to-t sm:from-bg/95 sm:via-bg/80 sm:to-transparent sm:px-6 sm:pt-14"}>
              <div key={index} className="mx-auto max-w-3xl" style={{ animation: "atlas-caption-in 420ms ease-out both" }} aria-live="polite">
                {moveLabel && <p className="mb-1 font-mono text-sm font-semibold text-accent">{moveLabel}</p>}
                <p className="text-base leading-snug sm:text-xl">{beat.caption}</p>
              </div>

              {study && <div className="lesson-film-legend" aria-label="Diagram colour key"><span><i style={{ background: TONE.gold }} />White move</span><span><i style={{ background: TONE.violet }} />Black move</span><span><i style={{ background: TONE.red }} />Threat</span><span><i style={{ background: TONE.green }} />Support / safe square</span><span><i style={{ background: TONE.blue }} />Plan</span></div>}

              {/* Progress */}
              <div className="mx-auto mt-3 flex max-w-3xl gap-1" role="group" aria-label="Lesson steps">
                {frames.map((f, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    aria-label={`Step ${i + 1}${i === index ? ", current" : ""}`}
                    className="group h-4 flex-1 py-1.5"
                    style={{ flexGrow: durations[i] }}
                  >
                    <span className="relative block h-1 overflow-hidden rounded-full bg-border group-hover:bg-muted">
                      {i < index && <span className="absolute inset-0 bg-accent" />}
                      {i === index && <span ref={fill} className="absolute inset-y-0 left-0 bg-accent" style={{ width: "0%" }} />}
                    </span>
                  </button>
                ))}
              </div>

              {/* Controls */}
              <div className="mx-auto mt-2 flex max-w-3xl items-center gap-1 text-sm">
                <IconButton label="Previous step" onClick={() => goTo(index - 1)} disabled={index === 0}>
                  <path d="M6 6h2v12H6zM9.5 12l8.5 6V6z" />
                </IconButton>
                {ended ? (
                  <IconButton label="Replay" onClick={replay} big>
                    <path d="M12 5V2L7 6l5 4V7a5 5 0 1 1-5 5H5a7 7 0 1 0 7-7z" />
                  </IconButton>
                ) : (
                  <IconButton label={playing ? "Pause" : "Play"} onClick={() => setPlaying((p) => !p)} big>
                    {playing ? <path d="M7 5h4v14H7zM13 5h4v14h-4z" /> : <path d="M8 5v14l11-7z" />}
                  </IconButton>
                )}
                <IconButton label="Next step" onClick={() => goTo(index + 1)} disabled={index === frames.length - 1}>
                  <path d="M16 6h2v12h-2zM6 18l8.5-6L6 6z" />
                </IconButton>
                <span className="ml-2 tabular-nums text-muted">
                  {index + 1} / {frames.length}
                </span>
                <div className="ml-auto flex items-center gap-1" role="group" aria-label="Speed">
                  {SPEEDS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSpeed(s)}
                      aria-pressed={speed === s}
                      className={`rounded px-1.5 py-0.5 text-xs tabular-nums ${speed === s ? "bg-accent-soft text-accent" : "text-muted hover:text-text"}`}
                    >
                      {s}×
                    </button>
                  ))}
                </div>
              </div>
            </div>
        </>
      )}
    </div>
    {study && <aside className="lesson-film-chapters" aria-label="Visual lesson chapters"><header><strong>Follow the idea</strong><p>{frames.length} scenes · Select any scene to study it at your pace.</p></header><div>{frames.map((f, i) => <button key={i} aria-current={started && index === i ? "step" : undefined} onClick={() => { setStarted(true); setPlaying(false); goTo(i); }}><span>{String(i + 1).padStart(2, "0")}</span><p>{f.beat.caption}</p></button>)}</div></aside>}
    </div>
  );
}

function IconButton({ children, label, onClick, disabled, big }: { children: React.ReactNode; label: string; onClick: () => void; disabled?: boolean; big?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`grid place-items-center rounded-full transition hover:bg-accent-soft disabled:opacity-30 ${big ? "h-10 w-10 bg-surface-2/80" : "h-8 w-8"}`}
    >
      <svg width={big ? 22 : 18} height={big ? 22 : 18} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        {children}
      </svg>
    </button>
  );
}

function ChipButton({ children, active, onClick, label }: { children: React.ReactNode; active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      className={`rounded-full px-3 py-1 text-xs font-medium backdrop-blur ${active ? "bg-accent text-accent-ink" : "bg-surface-2/80 hover:bg-accent-soft"}`}
    >
      {children}
    </button>
  );
}
